import { Faders } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Form } from "react-aria-components";
import { type InstanceSettings, settingsQuery } from "@/components/admin/api";
import { adminError } from "@/components/admin/errors";
import { QueryBoundary } from "@/components/admin/shared";
import { PriorityIcon, setPriorityStyle } from "@/components/domain/PriorityIcon";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Button,
  Callout,
  FormSection,
  InlineError,
  Radio,
  RadioGroup,
  RowSkeleton,
  Select,
  Switch,
  TextField,
} from "@/components/ui";
import { api } from "@/lib/api";
import { LOCALES } from "@/lib/locale";
import { type SetupStatus, setupStatusQuery } from "@/lib/queries";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/admin/instance")({ component: InstancePage });

function InstancePage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_instance() }]);
  const q = useQuery(settingsQuery);
  return (
    <Page title={m.adm_nav_instance()} icon={Faders} meta={m.adm_inst_meta()} width="full" className="max-w-5xl">
      <QueryBoundary query={q} skeleton={<RowSkeleton rows={5} />} needs="instance.admin">
        {(settings) => <SettingsForm settings={settings} />}
      </QueryBoundary>
    </Page>
  );
}

/** Same limit as the server (`MAX_TRASH_RETENTION_DAYS`). */
const MAX_TRASH_DAYS = 3650;

const P_SCALE = [1, 2, 3, 4] as const;
const WORDS = () => [m.priority_urgent(), m.priority_high(), m.priority_medium(), m.priority_low()];

function PriorityPreview({ style }: { style: "p" | "word" }) {
  const labels = style === "p" ? ["P0", "P1", "P2", "P3"] : WORDS();
  return (
    <span className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-fg">
      {P_SCALE.map((v, i) => (
        <span key={v} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-flex">
            <PriorityIcon value={v} size={14} />
          </span>
          {labels[i]}
        </span>
      ))}
    </span>
  );
}

function SettingsForm({ settings }: { settings: InstanceSettings }) {
  const qc = useQueryClient();
  const [name, setName] = useState(settings.name);
  const [locale, setLocale] = useState<string>(settings.defaultLocale);
  const [style, setStyle] = useState(settings.priorityStyle);
  const [require2fa, setRequire2fa] = useState(settings.require2faForAdmins);
  const [trash, setTrash] = useState(String(settings.trashRetentionDays));
  const [trashError, setTrashError] = useState<string | undefined>();
  const trashDays = /^\d+$/.test(trash.trim()) ? Number(trash.trim()) : Number.NaN;
  const [nameError, setNameError] = useState<string | undefined>();

  const dirty =
    name.trim() !== settings.name ||
    locale !== settings.defaultLocale ||
    style !== settings.priorityStyle ||
    require2fa !== settings.require2faForAdmins ||
    trash.trim() !== String(settings.trashRetentionDays);

  const save = useMutation({
    mutationFn: () =>
      api.patch<InstanceSettings>("/api/v1/admin/settings", {
        name: name.trim(),
        defaultLocale: locale,
        priorityStyle: style,
        require2faForAdmins: require2fa,
        trashRetentionDays: trashDays,
      }),
    onSuccess: (next) => {
      qc.setQueryData(settingsQuery.queryKey, next);
      qc.setQueryData(setupStatusQuery.queryKey, (old: SetupStatus | undefined) =>
        old ? { ...old, instanceName: next.name } : old,
      );
      setPriorityStyle(next.priorityStyle);
      toast.show({ title: m.settings_saved(), tone: "success" });
    },
  });

  const reset = () => {
    setName(settings.name);
    setLocale(settings.defaultLocale);
    setStyle(settings.priorityStyle);
    setRequire2fa(settings.require2faForAdmins);
    setTrash(String(settings.trashRetentionDays));
    setTrashError(undefined);
    setNameError(undefined);
  };

  return (
    <Form
      validationBehavior="aria"
      className="flex flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) {
          setNameError(m.validation_required());
          return;
        }
        if (!Number.isInteger(trashDays) || trashDays < 0 || trashDays > MAX_TRASH_DAYS) {
          setTrashError(m.adm_inst_trash_invalid({ max: MAX_TRASH_DAYS }));
          return;
        }
        setNameError(undefined);
        setTrashError(undefined);
        save.mutate();
      }}
    >
      {save.isError ? <InlineError className="mb-4" message={adminError(save.error)} /> : null}

      <FormSection title={m.adm_inst_general()} description={m.adm_inst_general_hint()}>
        <TextField
          label={m.adm_inst_name()}
          name="name"
          autoComplete="organization"
          value={name}
          onChange={(v) => {
            setName(v);
            setNameError(undefined);
          }}
          error={nameError}
        />
        <Select
          label={m.setup_language()}
          description={m.adm_inst_language_hint()}
          items={LOCALES.map((l) => ({ id: l.value, label: l.label }))}
          selectedKey={locale}
          onSelectionChange={setLocale}
        />
      </FormSection>

      <FormSection title={m.adm_inst_priority()} description={m.adm_inst_priority_hint()} wide>
        <RadioGroup
          label={m.adm_inst_priority()}
          labelHidden
          value={style}
          onChange={(v) => setStyle(v as "p" | "word")}
          className="max-w-xl"
        >
          <Radio
            value="p"
            label={m.adm_inst_priority_p()}
            description={m.adm_inst_priority_p_hint()}
            preview={<PriorityPreview style="p" />}
          />
          <Radio
            value="word"
            label={m.adm_inst_priority_word()}
            description={m.adm_inst_priority_word_hint()}
            preview={<PriorityPreview style="word" />}
          />
        </RadioGroup>
      </FormSection>

      <FormSection title={m.adm_inst_security()} description={m.adm_inst_security_hint()}>
        <Switch isSelected={require2fa} onChange={setRequire2fa} description={m.adm_inst_2fa_hint()}>
          {m.adm_inst_2fa()}
        </Switch>
        {require2fa && !settings.require2faForAdmins ? <Callout tone="info">{m.adm_inst_2fa_note()}</Callout> : null}
      </FormSection>

      <FormSection title={m.adm_inst_trash()} description={m.adm_inst_trash_hint()}>
        <TextField
          label={m.adm_inst_trash_days()}
          name="trashRetentionDays"
          type="number"
          inputMode="numeric"
          value={trash}
          onChange={(v) => {
            setTrash(v);
            setTrashError(undefined);
          }}
          error={trashError}
          description={trashDays === 0 ? m.adm_inst_trash_never() : m.adm_inst_trash_days_hint()}
          className="max-w-xs"
        />
      </FormSection>

      {dirty ? (
        <div className="anim-fade sticky bottom-0 -mx-1 flex items-center gap-2 border-t border-line bg-canvas px-1 py-4">
          <Button type="submit" variant="primary" isPending={save.isPending}>
            {m.common_save()}
          </Button>
          <Button variant="ghost" onPress={reset}>
            {m.adm_inst_discard()}
          </Button>
        </div>
      ) : null}
    </Form>
  );
}
